import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  EllipsisVertical,
  Plus,
  Settings,
  Trash2,
  Webhook as WebhookIcon,
  Workflow,
} from "lucide-react";
import InfiniteScroll from "react-infinite-scroll-component";
import Table from "@/components/ui/shared/table/Table";
import TableBody from "@/components/ui/shared/table/TableBody";
import TableCell from "@/components/ui/shared/table/TableCell";
import TableHeader from "@/components/ui/shared/table/TableHeader";
import TableRow from "@/components/ui/shared/table/TableRow";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useFluxos } from "@/hooks/useFluxos";

const PAGE_SIZE = 50;

const Fluxos: React.FC = () => {
  const navigate = useNavigate();
  const { fluxos, deleteFluxo, loading } = useFluxos();
  const [openPopoverId, setOpenPopoverId] = useState<string | null>(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [fluxos.length]);

  const visibleFluxos = useMemo(
    () => fluxos.slice(0, visibleCount),
    [fluxos, visibleCount]
  );

  const hasMore = visibleCount < fluxos.length;

  const loadMore = useCallback(() => {
    setVisibleCount((prev) => Math.min(prev + PAGE_SIZE, fluxos.length));
  }, [fluxos.length]);

  const handleEdit = (id: string) => {
    setOpenPopoverId(null);
    navigate(`/fluxos/editar/${id}`);
  };

  const handleDelete = async (id: string) => {
    setOpenPopoverId(null);
    if (confirm("Tem certeza que deseja excluir este fluxo?")) {
      await deleteFluxo(id);
    }
  };

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

  return (
    <div className="w-full flex h-[calc(100dvh-110px)] flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-[34px] sm:text-[36px] leading-tight font-bold text-foreground tracking-tight">
          Fluxos
        </h1>
        <button
          onClick={() => navigate("/fluxos/novo")}
          className="min-h-[40px] px-4 rounded-[12px] flex justify-center items-center gap-2 transition-colors bg-[#2563EB] hover:bg-[#1D4ED8] text-white shadow-sm dark:border dark:border-neutral-500 dark:bg-neutral-600 dark:hover:bg-neutral-500 dark:shadow-none dark:rounded-[6px]"
        >
          <p className="font-medium text-[14px]">Novo fluxo</p>
          <Plus size={18} className="opacity-80 dark:text-[#A1A1AA] dark:opacity-100" />
        </button>
      </div>

      <div className="flex-1 min-h-0">
        {loading ? (
          <div className="w-full h-full flex items-center justify-center border border-border rounded-[20px] bg-card shadow-sm">
            <div className="flex flex-col items-center gap-3">
              <div className="w-6 h-6 border-3 border-border border-t-primary rounded-full animate-spin" />
              <p className="text-muted-foreground text-sm">Carregando fluxos...</p>
            </div>
          </div>
        ) : fluxos.length === 0 ? (
          <div className="w-full h-full flex items-center justify-center border border-border rounded-[20px] bg-card shadow-sm">
            <div className="flex flex-col items-center gap-5 text-center p-8 max-w-md">
              <div className="w-16 h-16 bg-[#DBEAFE] rounded-full flex items-center justify-center dark:bg-blue-500/20">
                <Workflow className="w-8 h-8 text-[#2563EB] dark:text-[#60A5FA]" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-[#0F172A] mb-2 dark:text-neutral-50">
                  Nenhum fluxo encontrado
                </h3>
                <p className="text-[#64748B] text-sm dark:text-neutral-400">
                  Crie seu primeiro fluxo para montar templates de envio com texto, imagem, áudio, delay e assistente.
                </p>
              </div>
              <button
                onClick={() => navigate("/fluxos/novo")}
                className="flex items-center gap-2 px-5 py-2.5 bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-[12px] shadow-sm transition-colors duration-200"
              >
                <Plus size={18} />
                Criar fluxo
              </button>
            </div>
          </div>
        ) : (
          <div
            id="fluxos-scroll-container"
            className="w-full max-h-full overflow-auto border border-border rounded-[20px] bg-card shadow-sm table-scrollbar"
            style={{ height: "calc(100vh - 300px)" }}
          >
            <InfiniteScroll
              dataLength={visibleFluxos.length}
              next={loadMore}
              hasMore={hasMore}
              loader={
                <div className="flex justify-center items-center py-8">
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-6 h-6 border-3 border-border border-t-primary rounded-full animate-spin" />
                    <p className="text-muted-foreground text-sm">
                      Carregando mais fluxos...
                    </p>
                  </div>
                </div>
              }
              endMessage={null}
              scrollableTarget="fluxos-scroll-container"
            >
              <Table className="min-w-[700px]">
                <TableHeader className="border-b border-border sticky top-0 bg-card z-10">
                  <TableRow>
                    {["Fluxo", "Blocos", "Atualizado em", "Ações"].map((item) => (
                      <TableCell
                        key={item}
                        as="th"
                        className="!text-[#475569] !font-semibold !text-[13px] !py-4"
                      >
                        <div className="flex justify-start items-center gap-1">
                          <p>{item}</p>
                        </div>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleFluxos.map((fluxo) => (
                    <TableRow
                      key={fluxo.id}
                      className="hover:bg-[#F8FAFC] dark:hover:bg-hover transition-colors duration-200 border-b border-border/60 last:border-b-0"
                    >
                      <TableCell as="td" className="!py-4">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold text-[#0F172A] dark:text-neutral-50 dark:font-medium">
                            {fluxo.name}
                          </p>
                          {fluxo.settings?.webhookEnabled && (
                            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full border border-green-500/30 bg-green-500/10 text-green-600 text-[11px] font-medium dark:text-green-400">
                              <WebhookIcon size={11} />
                              Webhook
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell as="td" className="!py-4">
                        <p className="text-[#0F172A] dark:text-neutral-50">
                          {fluxo.blocks.length}{" "}
                          {fluxo.blocks.length === 1 ? "bloco" : "blocos"}
                        </p>
                      </TableCell>
                      <TableCell as="td" className="!py-4">
                        <p className="text-sm text-[#64748B] dark:text-neutral-400">
                          {formatDate(fluxo.updatedAt)}
                        </p>
                      </TableCell>
                      <TableCell as="td" className="!py-4">
                        <Popover
                          open={openPopoverId === fluxo.id}
                          onOpenChange={(open) => setOpenPopoverId(open ? fluxo.id : null)}
                        >
                          <PopoverTrigger asChild>
                            <button className="w-[34px] h-[34px] flex justify-center items-center border border-border bg-white hover:bg-[#F1F5F9] rounded-[10px] transition-colors duration-200 dark:border-[#3F3F46] dark:bg-transparent dark:hover:bg-[#27272A]">
                              <EllipsisVertical
                                size={18}
                                className="text-[#64748B] dark:text-neutral-400"
                              />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="!bg-card p-1 w-[130px] flex flex-col gap-1 border-border border rounded-xl shadow-md dark:!bg-[#18181B] dark:border-[#3F3F46] dark:rounded-[7px] dark:shadow-none">
                            <button
                              onClick={() => handleEdit(fluxo.id)}
                              className="w-full flex items-center gap-2 px-3 rounded-[7px] bg-transparent hover:bg-[#F1F5F9] min-h-[32px] text-foreground transition-colors duration-200 dark:hover:bg-[#27272A] dark:text-neutral-50"
                            >
                              <Settings size={16} className="text-[#2563EB] dark:text-[#60A5FA]" />
                              Editar
                            </button>
                            <div className="h-px bg-border mx-1 dark:bg-[#3F3F46]" />
                            <button
                              onClick={() => handleDelete(fluxo.id)}
                              className="w-full flex items-center gap-2 px-3 rounded-[7px] bg-transparent hover:bg-red-50 min-h-[32px] text-red-500 transition-colors duration-200 dark:hover:bg-[#27272A] dark:text-red-400"
                            >
                              <Trash2 size={16} />
                              Remover
                            </button>
                          </PopoverContent>
                        </Popover>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </InfiniteScroll>
          </div>
        )}
      </div>
    </div>
  );
};

export default Fluxos;
